import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { responsiveRem2Px } from 'utils/helpers';
import { BASE_MARGIN, BREAKPOINTS } from 'utils/constants';
import { useIsMediumScreen, useIsSmallScreen } from 'utils/hooks';
import { useIsKeyboardNavigation } from 'utils/useA11yNavigation';
import TooltipRenderer, { getImgUrl } from './TooltipRenderer';

const HOVER_DELAY = 400; // how much delay before the tooltip is shown + img loading delay.
const HIDE_DELAY = 0; // how much delay before the tooltip is hidden.

// Hide func of current/prev tooltip. Allow to hard close prev tooltip in case no mouseleave
let prevTooltipHide = null;
const clearOwnHide = (hideFunc) => {
  if (hideFunc === prevTooltipHide) prevTooltipHide = null;
};

const TooltipTrigger = (props) => {
  const { type, children, viewable, ...restProps } = props;
  let { pageGap } = props;
  const triggerEl = useRef();
  const hoverDelayTimer = useRef();
  const hideDelayTimer = useRef();
  const imgUrl = getImgUrl(viewable);

  // Status flags
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false); // New state to handle exit animation
  
  const isDismissed = useRef(false); // Track manual dismissal
  const isVisibleRef = useRef(isVisible);
  
  const isKeyboardNav = useIsKeyboardNavigation();
  const isKeyboardNavRef = useRef(isKeyboardNav);

  useEffect(() => {
    isKeyboardNavRef.current = isKeyboardNav;
  }, [isKeyboardNav]);

  const isSmallScreen = useIsSmallScreen();
  const isMediumScreen = useIsMediumScreen();
  if (pageGap === undefined) {
    pageGap = parseFloat(BASE_MARGIN.large);
    if (isSmallScreen) {
      pageGap = parseFloat(BASE_MARGIN.small);
    } else if (isMediumScreen) {
      pageGap = parseFloat(BASE_MARGIN.medium);
    }
  }

  // Actual unmount function
  const unmount = useCallback(() => {
    setIsVisible(false);
    setIsClosing(false);
    clearOwnHide(hide); // eslint-disable-line no-use-before-define
  }, []);

  const hide = useCallback((immediate = false) => {
    if (immediate) {
      unmount();
    } else {
      setIsClosing(true);
      // Fallback: strict unmount after animation time if renderer doesn't call back
      // Using 400ms (ANIMATION_DURATION + buffer)
      setTimeout(unmount, 400); 
    }
  }, [unmount]);

  const onMouseLeave = useCallback(() => {
    isDismissed.current = false; // Reset dismissal on leave
    clearTimeout(hoverDelayTimer.current);
    hideDelayTimer.current = setTimeout(hide, HIDE_DELAY);
  }, [hide]);

  const onLoad = useCallback(() => {
    // Remove mouse leave event listener after image has been lazy loaded
    // as mouse leave event listener will be handed over to TooltipRenderer after image is loaded
    triggerEl.current?.removeEventListener('mouseleave', onMouseLeave);
  }, [onMouseLeave]);

  useEffect(() => {
    if (!triggerEl.current || !imgUrl) {
      return undefined;
    }

    const element = triggerEl.current;

    const show = () => {
      // If we are already closing, cancel the close and stay open
      if (isClosing) {
         setIsClosing(false);
         return;
      }
      setIsVisible(true);
      isDismissed.current = false;

      // hide prev tooltip in case browser doesn't fire mouseleave for it
      if (prevTooltipHide && prevTooltipHide !== hide) prevTooltipHide(); // Triggers graceful close on prev
      prevTooltipHide = hide;
    };

    const maybeShow = (ignoreKeyboard = false) => {
      // Don't show if manually dismissed
      if (isDismissed.current) return;

      // no tooltip for small screens
      if (isVisibleRef.current || window.innerWidth < BREAKPOINTS.sm) {
        return;
      }

      if (isKeyboardNavRef.current && !ignoreKeyboard) {
        return;
      }

      // Close previous tooltip immediately to avoid overlap
      if (prevTooltipHide && prevTooltipHide !== hide) {
        prevTooltipHide();
      }

      clearTimeout(hideDelayTimer.current);
      hoverDelayTimer.current = setTimeout(show, HOVER_DELAY);
    };

    const onMouseMove = () => {
      if (!isKeyboardNavRef.current) {
        element.removeEventListener('mousemove', onMouseMove);
        return;
      }
      maybeShow(true);
    };

    const onHoverCleanup = () => {
      element.removeEventListener('mousemove', onMouseMove);
      element.removeEventListener('mouseleave', onHoverCleanup);
    };

    const onMouseEnter = () => {
      maybeShow();
      element.addEventListener('mouseleave', onMouseLeave);
      element.addEventListener('mousemove', onMouseMove);
      element.addEventListener('mouseleave', onHoverCleanup);
    };

    const onClick = () => {
      // Hide tooltip immediately when clicked
      clearTimeout(hoverDelayTimer.current);
      clearTimeout(hideDelayTimer.current);
      hide();
    };

    const onFocus = () => maybeShow();

    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('click', onClick);
    element.addEventListener('focus', onFocus);

    return () => {
      clearTimeout(hoverDelayTimer.current);
      clearTimeout(hideDelayTimer.current);
      clearOwnHide(hide);

      element.removeEventListener('mouseenter', onMouseEnter);
      element.removeEventListener('click', onClick);
      element.removeEventListener('focus', onFocus);
      element.removeEventListener('mousemove', onMouseMove);
      element.removeEventListener('mouseleave', onHoverCleanup);

      element.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [imgUrl, hide, onMouseLeave]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        isDismissed.current = true; // Mark as dismissed
        hide(); // Graceful hide (or immediate if you prefer)
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible, hide]);

  return (
    <div ref={triggerEl}>
      {children}

      {isVisible && (
        <TooltipRenderer
          triggerEl={triggerEl.current}
          hide={unmount} // Renderer calls this when animation finishes
          isClosing={isClosing} // Tell renderer to animate out
          pageGap={responsiveRem2Px(pageGap)}
          viewable={viewable}
          onLoad={onLoad}
          {...restProps}
        />
      )}
    </div>
  );
};

TooltipTrigger.propTypes = {
  children: PropTypes.element.isRequired,
  type: PropTypes.string,
  ...TooltipRenderer.props,
};

export default TooltipTrigger;
